import { useParams, Link } from 'react-router-dom';
import { SEOHead, BreadcrumbNav } from '@/components/feature/SEO';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();

  const category = categories.find((c) => c.slug === slug);

  if (!category) {
    return (
      <main className="pt-20 w-full px-4 md:px-6 py-14">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-2xl font-heading font-bold text-foreground-950 mb-4">
            分类未找到
          </h1>
          <Link to="/" className="text-sm text-primary-500 hover:text-primary-600 transition-colors">
            返回首页
          </Link>
        </div>
      </main>
    );
  }

  const categoryArticles = articles.filter((a) => a.category === slug);

  const breadcrumbs = [
    { name: '首页', url: '/' },
    { name: category.name, url: `/category/${slug}` },
  ];

  const collectionSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${category.name} - 制服诱惑`,
    description: category.description,
    url: `https://dressplay.github.io/category/${slug}`,
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: categoryArticles.map((article, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        item: {
          '@type': 'Article',
          name: article.title,
          url: `https://dressplay.github.io/article/${article.slug}`,
        },
      })),
    },
  };

  return (
    <main className="pt-20">
      <SEOHead
        title={category.name}
        description={category.description}
        keywords={`${category.name},制服诱惑,制服造型搭配,制服cosplay`}
        canonicalUrl={`https://dressplay.github.io/category/${slug}`}
        ogImage={category.image}
        breadcrumbs={breadcrumbs}
        schema={collectionSchema}
      />

      <BreadcrumbNav items={breadcrumbs} />

      <section className="relative w-full h-[280px] md:h-[360px] overflow-hidden">
        <img
          src={category.image}
          alt={category.name}
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/40"></div>
        <div className="relative z-10 w-full h-full flex flex-col items-center justify-center text-center px-4 md:px-6">
          <div className="w-16 h-16 md:w-20 md:h-20 flex items-center justify-center rounded-full bg-accent-500/80 mb-4">
            <i className={`${category.icon} text-2xl md:text-3xl text-white`}></i>
          </div>
          <h1 className="text-2xl md:text-4xl font-heading font-bold text-white mb-3">
            {category.name}
          </h1>
          <p className="text-sm md:text-base text-white/80 max-w-2xl leading-relaxed">
            {category.description}
          </p>
        </div>
      </section>

      <section className="w-full px-4 md:px-6 py-4 bg-background-100 border-b border-background-200">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-2 flex-wrap">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`px-4 py-1.5 text-xs rounded-full transition-colors whitespace-nowrap ${
                  cat.slug === slug
                    ? 'bg-primary-500 text-white'
                    : 'bg-background-50 text-foreground-600 hover:bg-background-200'
                }`}
              >
                {cat.name}
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-50">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-8">
            <h2 className="text-lg font-semibold text-foreground-950">
              共 <strong>{categoryArticles.length}</strong> 篇文章
            </h2>
          </div>

          {categoryArticles.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-foreground-500">该分类暂无文章</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {categoryArticles.map((article) => (
                <Link
                  key={article.slug}
                  to={`/article/${article.slug}`}
                  className="group bg-background-100 rounded-lg overflow-hidden hover:bg-background-200 transition-colors cursor-pointer"
                >
                  <div className="relative w-full h-52 overflow-hidden">
                    <img
                      src={article.image}
                      alt={article.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-5">
                    <h3 className="text-sm font-semibold text-foreground-950 mb-2 line-clamp-2 leading-snug group-hover:text-primary-500 transition-colors">
                      {article.title}
                    </h3>
                    <p className="text-xs text-foreground-500 line-clamp-2 leading-relaxed mb-3">
                      {article.excerpt}
                    </p>
                    <div className="flex items-center gap-3 text-xs text-foreground-400">
                      <span>{article.author}</span>
                      <span>{article.date}</span>
                      <span>{article.readTime}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>
    </main>
  );
}